import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { useSubdomain } from '../contexts/SubdomainContext';

const DEFAULT_BLUE = '#2B4B6A';
const DEFAULT_ORANGE = '#F58A5A';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [platformTheme, setPlatformTheme] = useState(null);
  const { company, isSubdomain, loading: subdomainLoading } = useSubdomain();

  // Fetch platform theme for main domain
  useEffect(() => {
    if (!isSubdomain) {
      const fetchPlatformTheme = async () => {
        try {
          const res = await api.get('/settings/public-theme');
          setPlatformTheme(res.data);
        } catch (error) {
          console.log('Using default theme');
        }
      };
      fetchPlatformTheme();
    }
  }, [isSubdomain]);

  // Get company branding colors (for subdomain) or platform theme (for main domain)
  const primaryColor = isSubdomain 
    ? (company?.theme_settings?.primary_color || DEFAULT_BLUE)
    : (platformTheme?.primary_color || DEFAULT_BLUE);
  const accentColor = isSubdomain 
    ? (company?.theme_settings?.accent_color || DEFAULT_ORANGE)
    : (platformTheme?.secondary_color || DEFAULT_ORANGE);
  const buttonBg = isSubdomain 
    ? (company?.theme_settings?.primary_color || DEFAULT_BLUE)
    : (platformTheme?.button_primary_bg || DEFAULT_BLUE);
  const buttonText = isSubdomain 
    ? '#FFFFFF'
    : (platformTheme?.button_primary_text || '#FFFFFF');
  const pageBgColor = isSubdomain 
    ? (company?.theme_settings?.external_page_bg || '#F9FAFB')
    : (platformTheme?.page_bg || '#F9FAFB');
  
  const companyName = company?.name || 'Trados';
  const companyLogo = company?.logo_url;

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const getLogoUrl = (url) => {
    if (!url) return null;
    return resolveFileUrl(url, API_URL);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking subdomain
  if (subdomainLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="w-10 h-10 border-3 rounded-full animate-spin"
             style={{ borderColor: '#E5E7EB', borderTopColor: '#2B4B6A', borderWidth: '3px' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: pageBgColor }}>
      {/* Header */}
      <PublicHeader />
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            {!submitted ? (
              <>
                {/* Icon */}
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${primaryColor}15` }}>
                  <Mail className="w-8 h-8" style={{ color: primaryColor }} />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Forgot your password?</h2>
                <p className="text-gray-600 text-center mb-8">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                        style={{ '--tw-ring-color': primaryColor }}
                        placeholder="Enter your email"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl font-semibold transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: buttonBg, color: buttonText }}
                    data-testid="forgot-submit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      'Send Reset Instructions'
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${primaryColor}15` }}>
                  <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
                <p className="text-gray-600 mb-6">
                  If an account with <strong>{email}</strong> exists, we've sent password reset instructions to that email address.
                </p>
                <p className="text-sm text-gray-500 mb-8">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => setSubmitted(false)}
                    className="font-medium hover:underline"
                    style={{ color: primaryColor }}
                  >
                    try again
                  </button>
                </p>
              </div>
            )}

            <Link
              to="/login"
              className="flex items-center justify-center gap-2 mt-6 text-sm font-medium transition hover:opacity-80"
              style={{ color: primaryColor }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default ForgotPasswordPage;
